import {
  Card,
  Space,
  Typography,
  Upload,
  Button,
  message,
  notification,
} from "antd";
import {
  DeleteOutlined,
  PlayCircleOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { useState } from "react";
import { uploadZipCfdis } from "api/generated";
import { useClient } from "hooks/useClient";
import { isAxiosError } from "axios";

const { Paragraph, Text } = Typography;

export default function UploadCFDIs() {
  const { client } = useClient();
  const [fileList, setFileList] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);

  const beforeSelect = (file: File) => {
    const isZip =
      file.type === "application/zip" ||
      file.name.toLowerCase().endsWith(".zip");
    if (!isZip) {
      message.error("Solo se permiten archivos ZIP");
      return Upload.LIST_IGNORE;
    }
    setFileList([file as File]);
    message.success(`${file.name} listo para procesar`);
    return false;
  };

  const handleRemove = () => {
    setFileList([]);
  };

  const uploadZipFile = () => {
    if (fileList.length === 0) {
      message.warning("Selecciona primero un archivo ZIP");
      return;
    }
    const zipFile = fileList[0] as File;

    const formData = new FormData();
    formData.append("zipFile", zipFile);
    const blob = new Blob([zipFile], { type: "application/zip" });

    uploadZipCfdis({ client, body: { zipFile: blob } })
      .then(({ data }) => {
        notification.success({
          message: "Proceso iniciado",
          description: (
            <Space direction="vertical">
              <Text>Archivo: {zipFile.name}</Text>
              {data?.id && <Text>Se procesarán {data.countCfdis} CFDIs</Text>}
            </Space>
          ),
        });
        setFileList([]);
      })
      .catch((err) => {
        if (isAxiosError(err)) {
          notification.error({
            message: "Error al subir CFDIs",
            description: `No fue posible procesar el archivo ZIP ${err.message}`,
          });
        }
      })
      .finally(() => {
        setUploading(false);
      });
  };

  return (
    <div>
      <Card
        style={{ paddingTop: 20 }}
        title={
          <div>
            <Space>
              <UploadOutlined />
              Gestión de Carga de CFDIs para asignación a ventas, recepciones o
              traspasos
            </Space>
            <div style={{ fontSize: "12px", marginTop: 12 }}>
              <Paragraph strong>
                Subida de CFDI – Vinculación automática
              </Paragraph>

              <Paragraph>
                Sube un archivo ZIP que contenga tus CFDIs en formato XML
                timbrados por el SAT.
              </Paragraph>

              <Paragraph strong>¿Qué hará la plataforma?</Paragraph>
              <ul style={{ paddingLeft: 20, marginBottom: 12 }}>
                <li>
                  <Text>
                    Descomprimirá el archivo ZIP y revisará cada CFDI.
                  </Text>
                </li>
                <li>
                  <Text>
                    Identificará el IUS (Identificador Único de Servicio) en la
                    descripción del comprobante.
                  </Text>
                </li>
                <li>
                  <Text>
                    Si encuentra coincidencias, vinculará el CFDI con la venta
                    correspondiente en la plataforma.
                  </Text>
                </li>
              </ul>

              <Paragraph>
                <Text strong>Nota:</Text> El tiempo de procesamiento dependerá
                de la cantidad de comprobantes incluidos en tu archivo.
              </Paragraph>

              <Paragraph>
                <Text strong>Acción final:</Text> Una vez que hayas seleccionado
                tu archivo, presiona el botón <i>“Iniciar proceso”</i> para
                comenzar la validación y vinculación de tus CFDIs.
              </Paragraph>
            </div>
          </div>
        }
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          <Upload
            multiple={false}
            accept=".zip"
            beforeUpload={beforeSelect}
            onRemove={handleRemove}
            fileList={fileList.map((f) => ({
              uid: "1",
              name: (f as File).name,
              status: "done",
            }))}
          >
            {fileList.length === 0 && (
              <Button icon={<UploadOutlined />} disabled={uploading}>
                Seleccionar archivo ZIP
              </Button>
            )}
          </Upload>

          <Space>
            <Button
              type="primary"
              icon={<PlayCircleOutlined />}
              onClick={uploadZipFile}
              loading={uploading}
              disabled={fileList.length === 0}
            >
              Iniciar proceso
            </Button>
            {fileList.length > 0 && (
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={handleRemove}
                disabled={uploading}
              >
                Quitar archivo
              </Button>
            )}
          </Space>
        </Space>
      </Card>
    </div>
  );
}
